import React from "react";
import { Plus, X } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

export const MAX_SOURCE_LINKS = 20;
export const MAX_LINK_LENGTH = 2048;
export const MAX_LABEL_LENGTH = 80;

const LINK_PATTERN = /^https?:\/\//i;

// The one place the URL scheme rule lives. Mirrors the backend's src/app/utils/source_urls.py.
export const isValidLink = (link) => LINK_PATTERN.test(link || "");

/**
 * Coerce whatever the server returned into editable {url, label} rows.
 *
 * Entries arrive as objects for anything saved since labels shipped, and as bare strings for
 * everything stored before: 138 production rows at the time of writing. Both must render.
 */
export const toEntries = (raw) =>
  (raw || []).map((entry) =>
    typeof entry === "string"
      ? { url: entry, label: "" }
      : { url: entry?.url || "", label: entry?.label || "" }
  );

// Change detection in FileList.js hashes this function's output field by field, so a new field
// here needs a matching change there or an edit touching only it would read as "unchanged".
// Normalize for submission: trim, drop rows with no url, drop empty labels, dedupe by url
// keeping the first occurrence so its label wins.
export const normalizeLinks = (entries) => {
  const out = [];
  const seen = new Set();
  for (const entry of toEntries(entries)) {
    const url = entry.url.trim();
    const label = entry.label.trim();
    if (!url || seen.has(url)) continue;
    seen.add(url);
    out.push(label ? { url, label } : { url });
  }
  return out;
};

// Validate one file's links. Returns null when valid, or a translation key on error.
export const validateLinks = (entries) => {
  const cleaned = normalizeLinks(entries);
  if (cleaned.length > MAX_SOURCE_LINKS) return "sourceUrlsTooMany";
  for (const entry of cleaned) {
    if (!isValidLink(entry.url)) return "sourceUrlInvalid";
    if (entry.url.length > MAX_LINK_LENGTH) return "sourceUrlTooLong";
    if ((entry.label || "").length > MAX_LABEL_LENGTH) return "sourceLabelTooLong";
  }
  return null;
};

/**
 * Editable list of source links for one file, each with an optional short name.
 *
 * value: array of {url, label} (either may be empty while the user types). Bare strings from
 *        the server are accepted and coerced.
 * onChange: receives the new array of {url, label}
 *
 * Removing the last row yields one empty row, not [], so a row stays on screen. Callers must
 * therefore decide "has links?" with normalizeLinks(value).length, never value.length.
 */
const SourceLinksField = ({ value, onChange }) => {
  const { translate } = useLanguage();
  const coerced = toEntries(value);
  const rows = coerced.length > 0 ? coerced : [{ url: "", label: "" }];
  const atMax = rows.length >= MAX_SOURCE_LINKS;

  // Nudge, not a rule: an optional field nobody fills would leave the agent guessing between
  // links whose URLs say nothing, which is the problem labels exist to solve.
  const filled = rows.filter((r) => r.url.trim());
  const showHint = filled.length > 1 && filled.every((r) => !r.label.trim());

  const updateAt = (index, field, newValue) => {
    const updated = rows.map((row, i) => (i === index ? { ...row, [field]: newValue } : row));
    onChange(updated);
  };

  const removeAt = (index) => {
    const updated = rows.filter((_, i) => i !== index);
    onChange(updated.length > 0 ? updated : [{ url: "", label: "" }]);
  };

  return (
    <div className="space-y-2">
      {/* Column headers, because once a row is filled its placeholders disappear and nothing
          else says which box is which. Mirrors the row's flex columns exactly, including a
          spacer for the remove button, or the headers would sit offset from their inputs.
          aria-hidden: each input already carries a full aria-label, so announcing these too
          would just duplicate it. */}
      <div className="flex flex-wrap items-center gap-2" aria-hidden="true">
        <span className="grow basis-48 min-w-0 text-xs font-medium text-[var(--color-text-muted)]">
          {translate("sourceColumnLink")}
        </span>
        <span className="w-48 max-w-full shrink-0 text-xs font-medium text-[var(--color-text-muted)]">
          {translate("sourceColumnName")}
        </span>
        {rows.length > 1 && <span className="w-6 shrink-0" />}
      </div>
      {rows.map((row, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <input
            type="url"
            maxLength={MAX_LINK_LENGTH}
            aria-label={translate("sourceLinkNumbered", { n: i + 1, total: rows.length })}
            placeholder={translate("sourceLinkPlaceholder")}
            value={row.url}
            onChange={(e) => updateAt(i, "url", e.target.value)}
            className="field-input grow basis-48 min-w-0"
          />
          <input
            type="text"
            maxLength={MAX_LABEL_LENGTH}
            aria-label={translate("sourceLabelNumbered", { n: i + 1, total: rows.length })}
            placeholder={translate("sourceLabelPlaceholder")}
            value={row.label}
            onChange={(e) => updateAt(i, "label", e.target.value)}
            className="field-input w-48 max-w-full shrink-0"
          />
          {rows.length > 1 && (
            <button
              type="button"
              onClick={() => removeAt(i)}
              title={translate("removeLink")}
              aria-label={translate("removeLinkNumbered", { n: i + 1, total: rows.length })}
              className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...rows, { url: "", label: "" }])}
        disabled={atMax}
        title={atMax ? translate("sourceUrlsTooMany", { max: MAX_SOURCE_LINKS }) : undefined}
        className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--color-mint)] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Plus className="h-3.5 w-3.5" />
        {translate("addLink")}
      </button>
      {showHint && (
        <p className="text-xs" role="status" style={{ color: "var(--color-text-muted)" }}>
          {translate("sourceLabelsHint")}
        </p>
      )}
    </div>
  );
};

export default SourceLinksField;
